import { AuthProvider } from '@kargotech/tms-core/auth';
import { TmsUIProvider } from '@kargotech/tms-ui';
import { PageSuspenseFallback } from '@kargotech/tms-ui/components';
import FlagProvider from '@unleash/proxy-client-react';
import { ConfigProvider } from 'antd';
import { get } from 'lodash-es';
import React, { Suspense, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import formErrorMessages from '~/translations/en_form';
import theme from '~/configurations/theme';
import UNLEASH_CONFIGS from '~/configurations/unleash';
import ApolloClientProvider from '~/contexts/ApolloClientProvider';
import AppProvider from '~/contexts/AppProvider';
import NavigationProvider from '~/contexts/NavigationProvider';
import ProfileProvider from '~/contexts/ProfileProvider';
import { APOLLO_CLIENTS } from '~/services/apollo';
import AppRoutes from '~/routes';
import { Helmet } from 'react-helmet';
import { t } from 'i18next';

interface WrapperProps {
  children?: React.ReactNode;
}

export function UIWrapper({ children }: WrapperProps) {
  const { i18n } = useTranslation();

  return (
    <ThemeProvider theme={theme}>
      <TmsUIProvider i18n={i18n}>
        {children}
      </TmsUIProvider>
    </ThemeProvider>
  );
}

export function AppWrapper({ children }: WrapperProps) {
  const handleLogout = useCallback(() => {
    Object.keys(APOLLO_CLIENTS).forEach((apolloClient) => {
      const cache = get(APOLLO_CLIENTS, `${apolloClient}.cache`);
      if (cache) {
        cache.reset();
      }
    });
  }, []);

  return (
    <UIWrapper>
      <Helmet title={t('translations:head_title.app')} />
      <AppProvider>
        <ConfigProvider
          form={{
            validateMessages: formErrorMessages.antd,
          }}
        >
          <AuthProvider
            apiClient={APOLLO_CLIENTS.PROFILE}
            onLogoutSuccess={handleLogout}
          >
            <ApolloClientProvider>
              <FlagProvider config={UNLEASH_CONFIGS}>
                <ProfileProvider>
                  <NavigationProvider>
                    {children}
                  </NavigationProvider>
                </ProfileProvider>
              </FlagProvider>
            </ApolloClientProvider>
          </AuthProvider>
        </ConfigProvider>
      </AppProvider>
    </UIWrapper>
  );
}

export default function App() {
  return (
    <AppWrapper>
      <Router>
        <Suspense fallback={<PageSuspenseFallback />}>
          <AppRoutes />
        </Suspense>
      </Router>
    </AppWrapper>
  );
}
